import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IconPlus } from "@pentabd/icons";
import { Button } from "@pentabd/ui";

import {
  Comments,
  ErrorModal,
  InputTextRow,
  SelectEmployeeModal,
  SuccessModal,
} from "@components";
import { ActionModal } from "./ActionModal";
import { AddPromotionEmployeeModal } from "./AddPromotionEmployeeModal";
import { InitiatePromotionAccordion } from "./InitiatePromotionAccordion";
import { InitiatePromotionHeader } from "./InitiatePromotionHeader";

import { INITIATE_PROMOTION } from "@validations/HRM/promotion/initiatePromotionFormValidation";

import {
  employeePromotionRecommendation,
  updateEmployeePromotionRecommendation,
} from "@api/initiatePromotion";
import { updatePromotionGroup } from "@api/promotions";
import { executeWorkflow, startWorkflow } from "@api/workflow";

import {
  PromotionDataType,
  PromotionDetailsType,
  RecommendationType,
} from "@type/HRM/promotions";

import { ROUTES } from "@constants/routes";
import { PROMOTION_STATUS, STATUS } from "@constants/status.hrm";
import { URLS } from "@constants/urls";
import { DECISION_TYPE } from "@constants/workflow-decision-type";
import { useUser } from "@hooks/useUser";

const { FORWARD } = DECISION_TYPE;
const { PROMOTIONS } = URLS;
const { RECOMMEND, REFUSE } = PROMOTION_STATUS;
const { SUCCESS } = STATUS;
const {
  HRM: { PROMOTION },
} = ROUTES;

export const InitialPromotionBody = ({
  promotionId,
  employeeMasterId,
  promotionData,
  isDisabled,
  showActionButton,
  handlePromotionDelete,
  handlePromotionUpdate,
  handlePromotionRecommendation,
  handleUpdatePromotionGroup,
}: {
  promotionId: string | number;
  employeeMasterId: string | number;
  promotionData: PromotionDataType;
  isDisabled: boolean;
  showActionButton: boolean;
  handlePromotionDelete: (arg: number) => void;
  handlePromotionUpdate: (promotionDetailsData: PromotionDetailsType) => void;
  handlePromotionRecommendation: (recommendation: RecommendationType) => void;
  handleUpdatePromotionGroup: (
    promotionDetails: PromotionDetailsType[],
  ) => void;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();

  const { positionId } = user;

  const [actionType, setActionType] = useState<string>("");
  const [promotionDetailsId, setPromotionDetailsId] = useState<number>();
  const [proceedToAction, setProceedToAction] = useState<boolean>(true);
  const [sendForApproval, setSendForApproval] = useState<boolean>(true);
  const [isRecommendationUpdate, setIsRecommendationUpdate] =
    useState<boolean>(false);
  const [redirectToListPage, setRedirectToListPage] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isExecutionActionModalOpen, setIsExecutionActionModalOpen] =
    useState<boolean>(false);
  const [isRecommendationActionModalOpen, setIsRecommendationActionModalOpen] =
    useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isAddEmployeeModalOpen, setIsAddEmployeeModalOpen] =
    useState<boolean>(false);
  const [isSelectEmployeeModalOpen, setIsSelectEmployeeModalOpen] =
    useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  // comments sorted based on the created time
  const sortedComments = promotionData?.promotionFlowDetail?.sort((a, b) =>
    dayjs(a.createdAt).diff(dayjs(b.createdAt)),
  );

  const methods = useForm({
    values: promotionData,
  });
  // const methods = useForm<InitiatePromotionSubmitFormDataType>({
  //   resolver: yupResolver(initiatePromotionSubmitFormValidation),
  //   values: promotionData,
  // });

  const { handleSubmit, register } = methods;

  // const onSubmit: SubmitHandler<InitiatePromotionSubmitFormDataType> = (
  //   data: InitiatePromotionSubmitFormDataType,
  const onSubmit: SubmitHandler<any> = () => {
    setIsSelectEmployeeModalOpen(true);
  };

  // hr action
  const sendForApprovalFunc = (prop: any) => {
    setIsLoading(true);

    startWorkflow(PROMOTIONS, promotionId, prop.nextReviewerId, (e) => {
      if (e.status === SUCCESS) {
        setRedirectToListPage(true);
        setIsSuccessModalOpen(true);
      } else setIsErrorModalOpen(true);
      setIsLoading(false);
    });
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const closeAddEmployeeModal = () => setIsAddEmployeeModalOpen(false);

  // add new employee in the promotion group
  const addEmployeeModalAction = (employeeId: any[]) => {
    setIsLoading(true);
    // filtering out the existing employees in the group
    const matchingIds = promotionData.promotionDetails
      .filter((item: any) => employeeId.includes(item.employee.id))
      .map((item: any) => item.employee.id);
    const nonMatchingIds = employeeId.filter((id) => !matchingIds.includes(id));

    closeAddEmployeeModal();
    updatePromotionGroup(
      promotionId as number,
      nonMatchingIds as number[],
      (e) => {
        if (e.status === SUCCESS) {
          setIsSuccessModalOpen(true);
          // updating local data
          handleUpdatePromotionGroup(e.data.promotionDetails);
        } else setIsErrorModalOpen(true);
        setIsLoading(false);
      },
    );
  };

  const closeActionModal = () => {
    setIsExecutionActionModalOpen(false);
    setIsRecommendationActionModalOpen(false);
  };

  const createSuccessAction = () => {
    setIsSuccessModalOpen(false);
    // optional redirection
    if (redirectToListPage) navigate(PROMOTION);
  };

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
      setIsAddEmployeeModalOpen(false);
      setRedirectToListPage(false);
      setIsExecutionActionModalOpen(false);
      setIsRecommendationActionModalOpen(false);
    };
    return reset();
  }, []);

  const handleExecutionActionModal = (action: string) => {
    setIsExecutionActionModalOpen(true);
    setActionType(action);
  };

  const handleRecommendationModal = (
    promotionDetailsId: number,
    actionType: string,
    update?: boolean,
  ) => {
    setIsRecommendationActionModalOpen(true);
    setActionType(actionType);
    setPromotionDetailsId(promotionDetailsId);
    // updating state to control the api call method
    update && setIsRecommendationUpdate(update);
  };

  // workflow
  const handleExecuteWorkflow = (data: any) => {
    setIsLoading(true);
    executeWorkflow(
      PROMOTIONS,
      promotionId,
      {
        processInstanceId: promotionData.processInstanceId,
        decisionType: actionType,
        nextReviewerId:
          actionType === FORWARD ? data.nextReviewerId : employeeMasterId,
        comment: data.actionComment,
      },
      (e) => {
        setIsExecutionActionModalOpen(false);
        if (e.status === SUCCESS) {
          setRedirectToListPage(true);
          setIsSuccessModalOpen(true);
        } else setIsErrorModalOpen(true);
        setIsLoading(false);
      },
    );
  };

  // recommendation action
  // currently not in use
  const handleRecommendationAction = (data: any) => {
    setIsLoading(true);
    // checking if the employee got accepted or rejected for promotion
    // this 0,1,2 is expected from the backend
    const approvedOrRejected =
      promotionData.isFinalState && actionType === RECOMMEND
        ? 1
        : promotionData.isFinalState && actionType === REFUSE
        ? 2
        : 0;

    // if its 2nd time then calling put method in the api else calling post method
    if (isRecommendationUpdate) {
      updateEmployeePromotionRecommendation(
        promotionId as number,
        promotionDetailsId as number,
        promotionData.promotionDetails?.find(
          (obj: PromotionDetailsType) =>
            obj.promotionDetailsId === promotionDetailsId,
        )?.recommendation?.recommendationId as number,
        {
          comments: data.actionComment,
          isRecommended: actionType == RECOMMEND ? true : false,
          approvedOrRejected,
        },
        (e) => {
          setIsRecommendationActionModalOpen(false);
          if (e.status === SUCCESS) {
            setIsSuccessModalOpen(true);
            handlePromotionRecommendation(e.data);
          } else setIsErrorModalOpen(true);
          setIsLoading(false);
        },
      );
    } else {
      employeePromotionRecommendation(
        promotionId as number,
        promotionDetailsId as number,
        {
          comments: data.actionComment,
          isRecommended: actionType == RECOMMEND ? true : false,
          approvedOrRejected,
        },
        (e) => {
          setIsRecommendationActionModalOpen(false);
          if (e.status === SUCCESS) {
            setIsSuccessModalOpen(true);
            handlePromotionRecommendation(e.data);
          } else setIsErrorModalOpen(true);
          setIsLoading(false);
        },
      );
    }
  };

  useEffect(() => {
    // code to check if user has taken recommendation action for all employees otherwise the final action buttons will be disabled
    // setProceedToAction(
    //   promotionData?.promotionDetails?.some(
    //     (obj: PromotionDetailsType) =>
    //       obj.recommendation.isRecommended !== null,
    //   ),
    // );
    setSendForApproval(
      promotionData?.promotionDetails?.some(
        (obj: PromotionDetailsType) =>
          // obj?.posting === null ||
          obj?.gradeId === null ||
          obj?.fromDate === null ||
          obj?.examinationScore === null ||
          obj?.unitId === null ||
          obj?.positionId === null ||
          obj?.unitPositionId === null,
      ),
    );
  }, [promotionData?.promotionDetails]);

  return (
    <>
      {/* MODAL SECTION start */}
      <>
        <SuccessModal
          isModalOpen={isSuccessModalOpen}
          action={createSuccessAction}
        />
        <ErrorModal
          isModalOpen={isErrorModalOpen}
          closeModal={closeErrorModal}
        />
        {/* for workflow: taking comment and submitting the workflow action */}
        <ActionModal
          title={actionType}
          isModalOpen={isExecutionActionModalOpen}
          closeModal={closeActionModal}
          handleExecutionAction={handleExecuteWorkflow}
          isLoading={isLoading}
        />
        {/* for recommendation: taking comment and submitting the recommendation action */}
        <ActionModal
          title={actionType}
          isModalOpen={isRecommendationActionModalOpen}
          closeModal={closeActionModal}
          handleRecommendationAction={handleRecommendationAction}
          isLoading={isLoading}
        />
        <SelectEmployeeModal
          isOpen={isSelectEmployeeModalOpen}
          onClose={() => setIsSelectEmployeeModalOpen(false)}
          action={sendForApprovalFunc}
          registeredName="nextReviewerId"
          isLoading={isLoading}
        />
      </>
      {/* MODAL SECTION end */}
      {/* showing this header component only after initiating the workflow */}
      {isDisabled && (
        <InitiatePromotionHeader
          candidateQuantity={promotionData?.promotionDetails?.length}
          isFinalState={promotionData?.isFinalState}
          handleActionModal={handleExecutionActionModal}
          showActionButton={showActionButton}
          proceedToAction={proceedToAction}
          positionId={positionId}
          isLoading={isLoading}
        />
      )}
      <div className="px-10 pb-10">
        {/* accordion section */}
        <InitiatePromotionAccordion
          promotionData={promotionData}
          isDisabled={isDisabled}
          showActionButton={showActionButton}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          setIsErrorModalOpen={setIsErrorModalOpen}
          handlePromotionDelete={handlePromotionDelete}
          handlePromotionUpdate={handlePromotionUpdate}
          recommendationAction={handleRecommendationModal}
        />
        {/* only show the add new employee button before initiation */}
        {!isDisabled && (
          <Button
            size="sm"
            fill="outline"
            type="light"
            onClick={() => setIsAddEmployeeModalOpen(true)}
            className="mt-16"
            loading={isLoading}
          >
            <IconPlus size={20} />
            {t("INITIATE_PROMOTION.ADD_NEW_EMPLOYEE")}
          </Button>
        )}

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* add new employee selection modal */}
            <AddPromotionEmployeeModal
              isModalOpen={isAddEmployeeModalOpen}
              closeModal={closeAddEmployeeModal}
              onSaveAction={addEmployeeModalAction}
              isLoading={isLoading}
            />
            <div className="mt-10 py-10 border-top">
              <InputTextRow
                label="INITIATE_PROMOTION.GROUP_NAME"
                register={{ ...register(INITIATE_PROMOTION.GROUP_NAME) }}
                disabled
              />
            </div>
            {promotionData?.promotionFlowDetail?.length > 0 && (
              <Comments data={sortedComments as any} />
            )}
            {!isDisabled && (
              <div className="d-flex justify-content-end gap-6 px-10 py-8 mt-10 border-top">
                <Button
                  size="sm"
                  fill="fill"
                  type="primary"
                  htmlType="submit"
                  disabled={sendForApproval}
                  loading={isLoading}
                >
                  {t("INITIATE_PROMOTION.BUTTON.SEND")}
                </Button>
              </div>
            )}
          </form>
        </FormProvider>
      </div>
    </>
  );
};
